import { useState, type ReactElement } from 'react'
import { Camera } from 'lucide-react'
import { Dialog, DialogContent } from './ui/dialog.js'
import type { ScanId, SavedPicture } from '../live/types.js'
import { useDemoStore } from '../state/store.js'

interface Props {
  scan: ScanId
}

/**
 * Horizontal strip of stills the AI captured during the scan, with the
 * customer-facing label under each. Tap to enlarge. Renders nothing when
 * there are no pictures (silent if absent — no placeholder).
 */
export const SavedPicturesStrip = ({ scan }: Props): ReactElement | null => {
  const { state } = useDemoStore()
  const pictures = state.savedPictures.filter(p => p.scan === scan)
  const [preview, setPreview] = useState<SavedPicture | null>(null)

  if (pictures.length === 0) return null

  return (
    <div className="mb-5">
      <div className="text-xs font-semibold tracking-wider text-primary uppercase mb-2 inline-flex items-center gap-1.5">
        <Camera className="h-3.5 w-3.5" /> Captured during scan
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {pictures.map(p => (
          <button
            key={`${p.takenAt}-${p.label}`}
            type="button"
            onClick={() => setPreview(p)}
            className="flex-shrink-0 w-24 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
          >
            <img
              src={p.dataUrl}
              alt={p.label}
              className="w-24 h-24 object-cover rounded-lg bg-muted"
            />
            <div className="text-xs text-muted-foreground mt-1 leading-tight">
              {p.label}
            </div>
          </button>
        ))}
      </div>

      <Dialog open={preview !== null} onOpenChange={(o) => { if (!o) setPreview(null) }}>
        <DialogContent>
          {preview && (
            <div>
              <img
                src={preview.dataUrl}
                alt={preview.label}
                className="w-full h-auto rounded-lg bg-muted"
              />
              <p className="text-sm text-muted-foreground mt-3 text-center">{preview.label}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
